import React, { useMemo, useEffect, useCallback } from "react";
import {
  Input,
  Select,
  Checkbox,
  Radio,
  Table,
  TreeSelect,
  Space,
  Spin,
} from "antd";
import { FormField, SpaceBetween, Box, Spinner } from "@cloudscape-design/components";
import { useController, Control } from "react-hook-form";
import { FormField as FormFieldType } from "../../../types/formSchema";
import { FIELD_EVENTS, StaticFilterValues } from "../../../constants/StaticFilterValues";
import { SCALE_CONFIG } from "../../../constants/scaleConfig";
import { UploadFieldComponent } from "./UploadField";
import { ColumnMapperFieldComponent } from "./ColumnMapper/ColumnMapperField";

const { TextArea } = Input;

interface FieldComponentProps {
  field: FormFieldType;
  control: Control<any>;
  dynamicOptions?: Record<string, any>;
  onFieldChange?: (fieldName: string, value: any) => void;
  workflowId: string; // Workflow identifier for API calls and state management
}

// Common search/filter utility function for value, label (dropdown/multiselect), and title (treeSelect)
const createSearchFilter = (inputValue: string, item: { value?: any; label?: any; title?: any }): boolean => {
  if (!inputValue) return true;
  const searchValue = inputValue.toLowerCase();
  const itemLabel = (item.label || item.title || '').toString().toLowerCase();
  const itemValue = (item.value || '').toString().toLowerCase();
  return itemLabel.includes(searchValue) || itemValue.includes(searchValue);
};

// Custom validation rule converter
const convertValidationRules = (field: FormFieldType) => {
  const rules: any = {};

  if (field.validation) {
    field.validation.forEach((rule) => {
      if (rule.required) {
        rules.required = rule.message || `${field.label} is required`;
      }
      if (rule.min) {
        rules.minLength = {
          value: rule.min,
          message: rule.message || `Minimum length is ${rule.min}`,
        };
      }
      if (rule.max) {
        rules.maxLength = {
          value: rule.max,
          message: rule.message || `Maximum length is ${rule.max}`,
        };
      }
      if (rule.pattern) {
        rules.pattern = {
          value: new RegExp(rule.pattern),
          message: rule.message || "Invalid format",
        };
      }
    });
  }

  return rules;
};

// Text Input Field
export const TextFieldComponent: React.FC<FieldComponentProps> = ({
  field,
  control,
  onFieldChange,
}) => {
  const {
    field: controllerField,
    fieldState: { error },
  } = useController({
    name: field.name,
    control,
    rules: convertValidationRules(field),
  });

  const { componentProps = {} } = field;

  return (
    <FormField
      label={field.label}
      description={field.tooltip}
      errorText={error?.message}
    >
      <Input
        {...componentProps}
        {...controllerField}
        placeholder={field.placeholder}
        disabled={!!field.disabled}
        readOnly={!!field.readonly}
        status={error ? "error" : ""}
        onChange={(event) => {
          const value = event.target.value;
          controllerField.onChange(value);
          onFieldChange?.(field.name, value);
        }}
      />
    </FormField>
  );
};

// Textarea Field
export const TextAreaFieldComponent: React.FC<FieldComponentProps> = ({
  field,
  control,
  onFieldChange,
}) => {
  const {
    field: controllerField,
    fieldState: { error },
  } = useController({
    name: field.name,
    control,
    rules: convertValidationRules(field),
  });

  const { componentProps = {} } = field;
  const textAreaField = field as any;

  return (
    <FormField
      label={field.label}
      description={field.tooltip}
      errorText={error?.message}
    >
      <TextArea
        {...componentProps}
        {...controllerField}
        placeholder={field.placeholder}
        disabled={!!field.disabled}
        readOnly={!!field.readonly}
        status={error ? "error" : ""}
        rows={textAreaField.rows || 4}
        onChange={(event) => {
          const value = event.target.value;
          controllerField.onChange(value);
          onFieldChange?.(field.name, value);
        }}
      />
    </FormField>
  );
};

// Dropdown Field
export const DropdownFieldComponent: React.FC<FieldComponentProps> = ({
  field,
  control,
  dynamicOptions,
  onFieldChange,
}) => {
  const {
    field: controllerField,
    fieldState: { error },
  } = useController({
    name: field.name,
    control,
    rules: convertValidationRules(field),
  });

  const { componentProps = {} } = field;
  const dropdownField = field as any;
  const options = dynamicOptions?.[field.name] || dropdownField.options || [];

  // Support dynamic field properties
  const dynamicDisabled = dynamicOptions?.[`${field.name}_disabled`];
  const dynamicPlaceholder = dynamicOptions?.[`${field.name}_placeholder`];
  const dynamicReadonly = dynamicOptions?.[`${field.name}_readonly`];

  // Conditional warning based on selected value
  const { warningMessage } = dropdownField;
  const warningText = warningMessage?.condition?.value === controllerField.value
    ? warningMessage?.text
    : undefined;

  return (
    <FormField
      label={field.label}
      description={field.tooltip}
      errorText={error?.message}
      warningText={warningText}
    >
      <Select
        {...componentProps}
        value={controllerField.value ?? null}
        onChange={(value) => {
          const newValue = !value ? null : value;
          controllerField.onChange(newValue);
          onFieldChange?.(field.name, newValue);
        }}
        onBlur={controllerField.onBlur}
        ref={controllerField.ref}
        placeholder={dynamicPlaceholder ?? field.placeholder ?? "Select an option"}
        disabled={dynamicDisabled ?? (!!field.disabled || (dynamicReadonly ?? !!field.readonly))}
        status={error ? "error" : ""}
        style={{ width: "100%", ...componentProps.style }}
        showSearch
        allowClear
        filterOption={(input, option) => createSearchFilter(input, option || {})}
        options={options.map((option: any) => ({
          label: option.label,
          value: option.value,
          disabled: option.disabled,
        }))}
      />
    </FormField>
  );
};

// Cascading Dropdown Field
export const CascadingDropdownFieldComponent: React.FC<FieldComponentProps> = ({
  field,
  control,
  dynamicOptions,
  onFieldChange,
}) => {
  const {
    field: controllerField,
    fieldState: { error },
  } = useController({
    name: field.name,
    control,
    rules: convertValidationRules(field),
  });

  const { componentProps = {} } = field;
  const options = dynamicOptions?.[field.name] || [];

  // Support dynamic field properties
  const dynamicDisabled = dynamicOptions?.[`${field.name}_disabled`];
  const dynamicPlaceholder = dynamicOptions?.[`${field.name}_placeholder`];
  const dynamicReadonly = dynamicOptions?.[`${field.name}_readonly`];

  const isDisabled =
    dynamicDisabled ?? (
      Boolean(field.disabled) ||
      (Boolean(field.dependsOn) && options.length === 0)
    );

  const placeholder = 
    dynamicPlaceholder ?? (
      field.dependsOn && options.length === 0
        ? `Select ${field.dependsOn} first`
        : (field.placeholder || "Select an option")
    );

  return (
    <FormField
      label={field.label}
      description={field.tooltip}
      errorText={error?.message}
    >
      <Select
        {...componentProps}
        value={controllerField.value ?? null}
        placeholder={placeholder}
        disabled={isDisabled || (dynamicReadonly ?? !!field.readonly)}
        status={error ? "error" : ""}
        style={{ width: "100%", ...componentProps.style }}
        loading={Boolean(options.length === 0 && field.dependsOn)}
        allowClear
        onChange={(value) => {
          const newValue = !value ? null : value;
          controllerField.onChange(newValue);
          onFieldChange?.(field.name, newValue);
        }}
        onBlur={controllerField.onBlur}
        ref={controllerField.ref}
        options={options.map((option: any) => ({
          label: option.label,
          value: option.value,
          disabled: option.disabled,
        }))}
      />
    </FormField>
  );
};

// Checkbox Field
export const CheckboxFieldComponent: React.FC<FieldComponentProps> = ({
  field,
  control,
  onFieldChange,
}) => {
  const {
    field: controllerField,
    fieldState: { error },
  } = useController({
    name: field.name,
    control,
    rules: convertValidationRules(field),
  });

  const { componentProps = {} } = field;

  return (
    <SpaceBetween size="xs">
      <Checkbox
        {...componentProps}
        {...controllerField}
        checked={controllerField.value || false}
        disabled={Boolean(field.disabled)}
        onChange={(event) => {
          const value = event.target.checked;
          controllerField.onChange(value);
          onFieldChange?.(field.name, value);
        }}
      >
        {field.label}
      </Checkbox>
      {field.tooltip && (
        <Box color="text-body-secondary" fontSize="body-s">
          {field.tooltip}
        </Box>
      )}
      {error && (
        <Box color="text-status-error" fontSize="body-s">
          {error.message}
        </Box>
      )}
    </SpaceBetween>
  );
};

// Radio Group Field
export const RadioGroupFieldComponent: React.FC<FieldComponentProps> = ({
  field,
  control,
  onFieldChange,
}) => {
  const {
    field: controllerField,
    fieldState: { error },
  } = useController({
    name: field.name,
    control,
    rules: convertValidationRules(field),
  });

  const { componentProps = {} } = field;
  const radioField = field as any;
  const options = radioField.options || [];

  return (
    <FormField
      label={field.label}
      description={field.tooltip}
      errorText={error?.message}
    >
      <Radio.Group
        {...componentProps}
        {...controllerField}
        onChange={(event) => {
          const value = event.target.value;
          controllerField.onChange(value);
          onFieldChange?.(field.name, value);
        }}
      >
        <Space direction="vertical">
          {options.map((option: any) => (
            <Radio key={option.value} value={option.value}>
              {option.label}
            </Radio>
          ))}
        </Space>
      </Radio.Group>
    </FormField>
  );
};

// Multiselect Field
export const MultiselectFieldComponent: React.FC<FieldComponentProps> = ({
  field,
  control,
  dynamicOptions,
  onFieldChange,
}) => {
  const {
    field: controllerField,
    fieldState: { error },
  } = useController({
    name: field.name,
    control,
    rules: convertValidationRules(field),
  });

  const { componentProps = {} } = field;
  const multiselectField = field as any;
  const options = dynamicOptions?.[field.name] || multiselectField.options || [];
  const maxTagCount = multiselectField.maxTagCount || 5;

  // Support dynamic field properties
  const dynamicDisabled = dynamicOptions?.[`${field.name}_disabled`];
  const dynamicPlaceholder = dynamicOptions?.[`${field.name}_placeholder`];

  return (
    <FormField
      label={field.label}
      description={field.tooltip}
      errorText={error?.message}
    >
      <Select
        {...componentProps}
        mode="multiple"
        value={controllerField.value ?? []}
        placeholder={dynamicPlaceholder ?? field.placeholder ?? "Select options"}
        disabled={dynamicDisabled ?? (!!field.disabled || !!field.readonly)}
        status={error ? "error" : ""}
        style={{ width: "100%", ...componentProps.style }}
        maxTagCount={maxTagCount}
        showSearch
        allowClear
        filterOption={(input, option) => createSearchFilter(input, option || {})}
        onChange={(values) => {
          const newValue = !values || values.length === 0 ? [] : values;
          controllerField.onChange(newValue);
          onFieldChange?.(field.name, newValue);
        }}
        onBlur={controllerField.onBlur}
        ref={controllerField.ref}
        options={options.map((option: any) => ({
          label: option.label,
          value: option.value,
        }))}
      />
    </FormField>
  );
};

// TreeSelect Field
export const TreeSelectFieldComponent: React.FC<FieldComponentProps> = ({
  field,
  control,
  dynamicOptions,
  onFieldChange,
}) => {
  const {
    field: controllerField,
    fieldState: { error },
  } = useController({
    name: field.name,
    control,
    rules: convertValidationRules(field),
  });

  const { componentProps = {} } = field;
  const treeSelectField = field as any;

  // Get loading nodes set from dynamic options
  const loadingNodes = dynamicOptions?.[`${field.name}_loadingNodes`] || new Set();
  const isSearchLoading = dynamicOptions?.[`${field.name}_searchLoading`] || false;

  // Get tree data from dynamic options or field configuration
  const baseTreeData = dynamicOptions?.[field.name] || treeSelectField.treeData || [];
  // Show empty tree data during search loading to clear options
  const treeData = isSearchLoading ? [] : baseTreeData;

  // Custom switcher icon - show spinner only for nodes that are loading
  const switcherIcon = ({ isLeaf, expanded, loading, data }: any) => {
    const nodeKey = data?.key || data?.value;
    if (loading || (loadingNodes instanceof Set && loadingNodes.has(nodeKey))) {
      return <Spin size="small" />;
    }
    return undefined;
  };

  // Check if multiple selection is enabled
  const isMultiple = treeSelectField.multiple || false;

  const maxTagCount = treeSelectField.maxTagCount || 5;

  // Handle loadData for dynamic loading
  const handleLoadData = treeSelectField.loadData ? (node: any) => {
    if (treeSelectField.filterType && onFieldChange) {
      return new Promise<void>((resolve) => {
        onFieldChange(`${field.name}${FIELD_EVENTS.LOAD_DATA}`, { node, filterType: treeSelectField.filterType });
        resolve();
      });
    }
    return Promise.resolve();
  } : undefined;

  const handleSearch = treeSelectField.filterType === StaticFilterValues.PRODUCT_ID ? (searchValue: string) => {
    if (onFieldChange) {
      onFieldChange(`${field.name}${FIELD_EVENTS.SEARCH}`, { searchValue, filterType: treeSelectField.filterType });
    }
  } : undefined;

  // Custom filter function to search both value and title
  const filterTreeNode = treeSelectField.filterType === StaticFilterValues.PRODUCT_ID 
    ? false 
    : (inputValue: string, treeNode: any) => createSearchFilter(inputValue, treeNode);

  return (
    <FormField
      label={field.label}
      description={field.tooltip}
      errorText={error?.message}
    >
      <TreeSelect
        {...componentProps}
        value={controllerField.value ?? (isMultiple ? [] : null)}
        placeholder={
          isSearchLoading ? "Searching..." : (
            field.placeholder ||
            (isMultiple ? "Select options" : "Select an option")
          )
        }
        disabled={!!field.disabled || !!field.readonly}
        status={error ? "error" : ""}
        multiple={isMultiple}
        treeData={treeData}
        showSearch
        allowClear
        loading={isSearchLoading}
        treeDefaultExpandAll={treeSelectField.treeDefaultExpandAll ?? true}
        treeCheckable={isMultiple}
        showCheckedStrategy={isMultiple ? TreeSelect.SHOW_PARENT : undefined}
        maxTagCount={maxTagCount}
        loadData={handleLoadData}
        onSearch={handleSearch}
        filterTreeNode={filterTreeNode}
        treeExpandAction="click"
        notFoundContent={isSearchLoading ? (
          <Box textAlign="center" padding="l">
            <Spinner size="normal" />
            <Box margin={{ top: "xs" }}>Searching...</Box>
          </Box>
        ) : (treeSelectField.filterType === StaticFilterValues.PRODUCT_ID ? "" : "No options available")}
        switcherIcon={switcherIcon}
        onChange={(value) => {
          // For multiple: clear to empty array, for single: clear to null
          const newValue = !value || (Array.isArray(value) && value.length === 0) 
            ? (isMultiple ? [] : null)
            : value;
          controllerField.onChange(newValue);
          onFieldChange?.(field.name, newValue);
        }}
        onBlur={controllerField.onBlur}
        ref={controllerField.ref}
        style={{ width: "100%" }}
      />
    </FormField>
  );
};

// Document Sections Table Field - Tree table with hierarchical structure
export const DocumentSectionsTableFieldComponent: React.FC<
  FieldComponentProps
> = ({ field, control, dynamicOptions, onFieldChange }) => {
  const {
    field: controllerField,
    fieldState: { error },
  } = useController({
    name: field.name,
    control,
    rules: convertValidationRules(field),
  });

  const { componentProps = {} } = field;
  const tableField = field as any;
  const tableData = tableField.tableData || [];
  const currentValue = controllerField.value || {};
  
  // Get custom error from dynamicOptions
  const customError = dynamicOptions?.[`${field.name}_error`];

  // Apply dynamic children if available
  const processedTableData = useMemo(() => {
    return tableData.map((item: any) => {
      const dynamicChildrenKey = `${field.name}_${item.id}_children`;
      if (dynamicOptions?.[dynamicChildrenKey]) {
        return {
          ...item,
          children: dynamicOptions[dynamicChildrenKey]
        };
      }
      return item;
    });
  }, [tableData, dynamicOptions, field.name]);

  // Get all leaf nodes (children) for bulk selection logic
  const getAllLeafNodes = (nodes: any[]): any[] => {
    let leafNodes: any[] = [];
    nodes.forEach((node) => {
      if (node.children && node.children.length > 0) {
        leafNodes = leafNodes.concat(getAllLeafNodes(node.children));
      } else {
        leafNodes.push(node);
      }
    });
    return leafNodes;
  };

  // Reset P&L by GL filter when product line changes
  useEffect(() => {
    const resetSignal = dynamicOptions?.['documentSections_executive_pnl_pnl_by_gl_reset'];
    const currentFilterValue = currentValue['pl_gl']?.filterValue;
    
    if (resetSignal && currentFilterValue !== undefined) {
      const newValue = {
        ...currentValue,
        'executive_pnl_pnl_by_gl': {
          ...currentValue['executive_pnl_pnl_by_gl'],
          filterValue: undefined
        }
      };
      controllerField.onChange(newValue);
      onFieldChange?.(field.name, newValue);
    }
  }, [dynamicOptions?.['documentSections_executive_pnl_pnl_by_gl_reset']]);

  // Memoize expensive calculations
  const leafNodes = useMemo(() => getAllLeafNodes(processedTableData), [processedTableData]);

  // Get checkbox states for bulk selection
  const getCheckboxStates = (
    checkboxType: "includeTable" | "includeCommentary"
  ) => {
    // Only consider nodes that have visible checkboxes
    const visibleLeafNodes = leafNodes.filter((item: any) => {
      const filterValue = currentValue[item.id]?.filterValue;
      const hasFilterValue = Array.isArray(filterValue) ? filterValue.length > 0 : !!filterValue;
      // Include node if it has no dependency OR if dependency is satisfied
      return !item.filterDependsOn || hasFilterValue;
    });

    const states = visibleLeafNodes.map(
      (item: any) => currentValue[item.id]?.[checkboxType] ?? item[checkboxType]
    );
    const checkedCount = states.filter(Boolean).length;

    return {
      allChecked: visibleLeafNodes.length > 0 && checkedCount === visibleLeafNodes.length,
      someChecked: checkedCount > 0 && checkedCount < visibleLeafNodes.length,
      noneChecked: checkedCount === 0,
    };
  };

  // Memoize checkbox states to avoid recalculation on every render
  const tableCheckboxStates = useMemo(() => ({
    includeTable: getCheckboxStates("includeTable"),
    includeCommentary: getCheckboxStates("includeCommentary")
  }), [leafNodes, currentValue]);

  // Handle individual checkbox changes
  const handleCheckboxChange = (
    itemId: string,
    checkboxType: "includeTable" | "includeCommentary",
    checked: boolean
  ) => {
    const newValue = {
      ...currentValue,
      [itemId]: {
        ...currentValue[itemId],
        [checkboxType]: checked,
        // If unchecking includeTable, also uncheck includeCommentary
        ...(checkboxType === "includeTable" && !checked && {
          includeCommentary: false,
        }),
      },
    };
    controllerField.onChange(newValue);
    onFieldChange?.(field.name, newValue);
    
    // Clear error if any checkbox is checked
    if (checked && customError) {
      onFieldChange?.(`${field.name}_clearError`, true);
    }
  };

  // Handle bulk checkbox changes (header checkbox)
  const handleBulkCheckboxChange = (
    checkboxType: "includeTable" | "includeCommentary",
    checked: boolean
  ) => {
    const newValue = { ...currentValue };

    leafNodes.forEach((item: any) => {
      const filterValue = currentValue[item.id]?.filterValue;
      const hasFilterValue = Array.isArray(filterValue) ? filterValue.length > 0 : !!filterValue;
      
      // Only update if checkbox is visible (no dependency OR dependency is satisfied)
      if (!item.filterDependsOn || hasFilterValue) {
        // For commentary, also check if table is enabled
        if (checkboxType === "includeCommentary" && checked) {
          const isTableChecked = newValue[item.id]?.includeTable ?? currentValue[item.id]?.includeTable ?? item.includeTable;
          if (!isTableChecked) {
            return; // Skip this item if table is not checked
          }
        }
        
        newValue[item.id] = {
          ...newValue[item.id],
          [checkboxType]: checked,
          // If unchecking includeTable, also uncheck includeCommentary
          ...(checkboxType === "includeTable" && !checked && {
            includeCommentary: false,
          }),
        };
      }
    });

    controllerField.onChange(newValue);
    onFieldChange?.(field.name, newValue);
    
    // Clear error if any checkbox is checked
    if (checked && customError) {
      onFieldChange?.(`${field.name}_clearError`, true);
    }
  };

  // Handle scale dropdown changes
  const handleScaleChange = (itemId: string, selectedValue: string) => {
    const newValue = {
      ...currentValue,
      [itemId]: {
        ...currentValue[itemId],
        scale: selectedValue,
      },
    };
    controllerField.onChange(newValue);
    onFieldChange?.(field.name, newValue);
  };

  // Handle filter dropdown changes
  const handleDropdownChange = (itemId: string, selectedValue: string | string[]) => {
    // For multiselect, limit to 10 selections
    let finalValue = selectedValue;
    if (Array.isArray(selectedValue) && selectedValue.length > 10) {
      finalValue = selectedValue.slice(0, 10);
    }
    
    const newValue = {
      ...currentValue,
      [itemId]: {
        ...currentValue[itemId],
        filterValue: finalValue,
      },
    };
    controllerField.onChange(newValue);
    onFieldChange?.(field.name, newValue);
  };

  // Handle filter multiselect changes
  const handleMultiselectChange = (itemId: string, selectedValues: string[]) => {
    // Check if this is a transition from empty to filled state
    const previousFilterValue = currentValue[itemId]?.filterValue || [];
    const isTransitioningFromEmpty = previousFilterValue.length === 0 && selectedValues.length > 0;
    
    const newValue = {
      ...currentValue,
      [itemId]: {
        ...currentValue[itemId],
        filterValue: selectedValues,
        // If filter is cleared (empty array), uncheck both checkboxes
        ...(selectedValues.length === 0 && {
          includeTable: false,
          includeCommentary: false,
        }),
        // Auto-enable checkboxes only when transitioning from empty to filled
        // This covers: initial selection (undefined → filled) and re-selection after clearing (empty → filled)
        // Does not override user's explicit choice when just modifying existing selections
        ...(isTransitioningFromEmpty && currentValue[itemId]?.includeTable !== true && {
          includeTable: true,
          includeCommentary: true,
        }),
      },
    };
    controllerField.onChange(newValue);
    onFieldChange?.(`${field.name}_${itemId}_filter`, selectedValues);
  };

  // Get dropdown options for a specific item
  const getDropdownOptions = (item: any) => {
    if (item.filterDependsOn && dynamicOptions?.[`${field.name}_${item.id}`]) {
      return dynamicOptions[`${field.name}_${item.id}`];
    }
    return item.filterOptions || [];
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 280,
      render: (text: string, record: any) => {
        // Parent nodes (with children) should be bold
        const isBold = record.isParent === true;
        return <Box fontWeight={isBold ? "bold" : "normal"}>{text}</Box>;
      },
    },
    {
      title: "Filter",
      key: "filter",
      width: 220,
      render: (_: any, record: any) => {
        // Show dash only if no filter
        if (!record.hasFilter) {
          return <Box>—</Box>;
        }

        const options = getDropdownOptions(record);
        const isDisabled = record.filterDependsOn && options.length === 0;
        const isMultiselect = record.filterType === 'multiselect';
        const currentSelections = currentValue[record.id]?.filterValue || [];
        const isMaxReached = isMultiselect && Array.isArray(currentSelections) && currentSelections.length >= 10;
        
        // Check if this specific filter is loading
        const isLoading = Boolean(dynamicOptions?.[`${field.name}_${record.id}_loading`]);

        // Handle multiselect filter type
        if (record.filterType === 'multiselect') {
          const selectedValues = currentValue[record.id]?.filterValue;
          const normalizedValue = Array.isArray(selectedValues) ? selectedValues : (selectedValues ? [selectedValues] : []);
          
          return (
            <Select
              mode="multiple"
              value={normalizedValue}
              onChange={(values) => handleMultiselectChange(record.id, values)}
              options={options.map((opt: any) => ({
                label: opt.label,
                value: opt.value,
              }))}
              placeholder={record.filterPlaceholder || "Select..."}
              style={{ width: "100%" }}
              maxTagCount={2}
              maxCount={record.maxCount}
              loading={isLoading}
              disabled={isDisabled || isLoading}
            />
          );
        }

        // Default dropdown filter
        return (
          <Select
            value={currentValue[record.id]?.filterValue || undefined}
            onChange={(value) => handleDropdownChange(record.id, value)}
            options={options.map((opt: any) => ({
              label: opt.label,
              value: opt.value,
              disabled: isMultiselect && Array.isArray(currentSelections) && 
                       currentSelections.length >= 10 && 
                       !currentSelections.includes(opt.value)
            }))}
            placeholder={record.filterPlaceholder || "Select..."}
            disabled={isDisabled || isLoading}
            loading={isLoading}
            mode={isMultiselect ? "multiple" : undefined}
            maxCount={isMultiselect ? 10 : undefined}
            status={isMaxReached ? "warning" : undefined}
            style={{ width: "100%" }}
          />
        );
      },
    },
    {
      title: () => {
        const { allChecked, someChecked } = tableCheckboxStates.includeTable;
        return (
          <Checkbox
            checked={allChecked}
            indeterminate={someChecked}
            onChange={(event) => {
              handleBulkCheckboxChange("includeTable", event.target.checked);
            }}
          >
            Include Table
          </Checkbox>
        );
      },
      key: "includeTable",
      width: 150,
      render: (_: any, record: any) => {
        // Only show checkboxes for leaf nodes (children)
        if (record.isParent === true) {
          return null;
        }

        // Hide checkboxes if filter depends on something and no filter value is selected
        // For multiselect, check if the array has items
        const filterValue = currentValue[record.id]?.filterValue;
        const hasFilterValue = Array.isArray(filterValue) ? filterValue.length > 0 : !!filterValue;
        
        // Hide checkbox completely if dependency is not met
        if (record.filterDependsOn && !hasFilterValue) {
          return null;
        }

        return (
          <Checkbox
            checked={
              currentValue[record.id]?.includeTable ?? record.includeTable
            }
            onChange={(event) => {
              handleCheckboxChange(
                record.id,
                "includeTable",
                event.target.checked
              );
            }}
          />
        );
      },
    },
    {
      title: () => {
        const { allChecked, someChecked } = tableCheckboxStates.includeCommentary;
        const { allChecked: allTableChecked, someChecked: someTableChecked } = tableCheckboxStates.includeTable;
        
        // Disable commentary header checkbox if no table checkboxes are checked
        const isDisabled = !allTableChecked && !someTableChecked;
        
        return (
          <Checkbox
            checked={allChecked}
            indeterminate={someChecked}
            disabled={isDisabled}
            onChange={(event) => {
              handleBulkCheckboxChange(
                "includeCommentary",
                event.target.checked
              );
            }}
          >
            Include Commentary
          </Checkbox>
        );
      },
      key: "includeCommentary",
      width: 200,
      render: (_: any, record: any) => {
        // Only show checkboxes for leaf nodes (children)
        if (record.isParent === true) {
          return null;
        }

        // Hide checkboxes if filter depends on something and no filter value is selected
        // For multiselect, check if the array has items
        const filterValue = currentValue[record.id]?.filterValue;
        const hasFilterValue = Array.isArray(filterValue) ? filterValue.length > 0 : !!filterValue;
        
        // Hide checkbox completely if dependency is not met
        if (record.filterDependsOn && !hasFilterValue) {
          return null;
        }

        // Check if the Include Table checkbox is checked
        const isTableChecked = currentValue[record.id]?.includeTable ?? record.includeTable;

        return (
          <Checkbox
            checked={
              currentValue[record.id]?.includeCommentary ??
              record.includeCommentary
            }
            disabled={!isTableChecked}
            onChange={(event) => {
              handleCheckboxChange(
                record.id,
                "includeCommentary",
                event.target.checked
              );
            }}
          />
        );
      },
    },
    {
      title: "Scale",
      key: "scale",
      width: 140,
      render: (_: any, record: any) => {
        // Only show scale dropdown for leaf nodes (children)
        if (record.isParent === true) {
          return null;
        }

        return (
          <Select
            value={
              currentValue[record.id]?.scale ?? record.scale ?? SCALE_CONFIG.DEFAULT
            }
            onChange={(value) => handleScaleChange(record.id, value)}
            options={[...SCALE_CONFIG.OPTIONS]}
            style={{ width: "100%" }}
          />
        );
      },
    },
  ];

  return (
    <FormField
      label={field.label}
      description={field.tooltip}
      errorText={customError || error?.message}
    >
      <Table
        {...componentProps}
        columns={columns}
        dataSource={processedTableData}
        rowKey="id"
        pagination={false}
        size="small"
        bordered
        expandable={{ defaultExpandAllRows: true }}
        style={{ width: "100%" }}
      />
    </FormField>
  );
};


// Field Registry - Maps field types to components
export const FieldRegistry = {
  text: TextFieldComponent,
  textarea: TextAreaFieldComponent,
  dropdown: DropdownFieldComponent,
  cascadingDropdown: CascadingDropdownFieldComponent,
  checkbox: CheckboxFieldComponent,
  radioGroup: RadioGroupFieldComponent,
  multiselect: MultiselectFieldComponent,
  treeSelect: TreeSelectFieldComponent,
  documentSectionsTable: DocumentSectionsTableFieldComponent,
  upload: UploadFieldComponent,
  columnMapper: ColumnMapperFieldComponent,
} as const;

export type FieldType = keyof typeof FieldRegistry;
